import { Progress, Skeleton } from '@mantine/core';
import DialogBox from '@xver/components/Atoms/DialogBox/DialogBox';
import { useTranslation } from 'react-i18next';

import { useContainerQuotaQuery } from '../hooks/useContainerQuotaQuery';

interface QuotaCardProps {
    containerId: string;
}

function quotaColor(percentage: number): string {
    if (percentage >= 100) return 'gray';
    if (percentage >= 75) return 'yellow';
    return 'teal';
}

const hours = (seconds: number) =>
    (seconds / 3600).toLocaleString(undefined, { maximumFractionDigits: 1 });

const compact = (n: number) =>
    new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(n);

interface QuotaRowProps {
    label: string;
    usedLimit: string;
    percentage: number;
}

function QuotaRow({ label, usedLimit, percentage }: QuotaRowProps) {
    const { t } = useTranslation('containerEngineInstance');
    const reached = percentage >= 100;

    return (
        <div className="w-full flex flex-col gap-1">
            <div className="w-full flex flex-row justify-between items-center">
                <span className="text-sm text-fg">{label}</span>
                <span className="text-xs text-fg/68">
                    {usedLimit} ({percentage}%)
                </span>
            </div>
            <Progress aria-label={label} value={percentage} color={quotaColor(percentage)} />
            {reached && <span className="text-xs text-fg/68">{t('quota.reached')}</span>}
        </div>
    );
}

export default function QuotaCard({ containerId }: QuotaCardProps) {
    const { t } = useTranslation('containerEngineInstance');
    const { data, isPending } = useContainerQuotaQuery(containerId);

    const loading = isPending && !data;

    const cpu = data?.quotas.cpu;
    const memory = data?.quotas.memory;
    const requests = data?.quotas.requests;

    return (
        <DialogBox
            label={t('quota.title')}
            description={t('quota.description')}
            classNames="p-4 gap-4"
        >
            {loading || !cpu || !memory || !requests ? (
                <div className="w-full flex flex-col gap-4">
                    {[0, 1, 2].map((i) => (
                        <div key={i} className="w-full flex flex-col gap-1">
                            <div className="w-full flex flex-row justify-between items-center">
                                <Skeleton height={14} width={80} />
                                <Skeleton height={14} width={100} />
                            </div>
                            <Skeleton height={8} width="100%" />
                        </div>
                    ))}
                </div>
            ) : (
                <>
                    <QuotaRow
                        label={t('quota.cpu')}
                        usedLimit={`${hours(cpu.used)} / ${hours(cpu.limit)} ${t('quota.cpuUnit')}`}
                        percentage={cpu.percentage_used}
                    />
                    <QuotaRow
                        label={t('quota.memory')}
                        usedLimit={`${hours(memory.used)} / ${hours(memory.limit)} ${t('quota.memoryUnit')}`}
                        percentage={memory.percentage_used}
                    />
                    <QuotaRow
                        label={t('quota.requests')}
                        usedLimit={`${compact(requests.used)} / ${compact(requests.limit)} ${t('quota.requestsUnit')}`}
                        percentage={requests.percentage_used}
                    />
                </>
            )}
        </DialogBox>
    );
}
